// Draft persistence adapter.
//
// The Zustand store owns the in-memory drafts map. This module mirrors it
// to localStorage so composer drafts survive a page reload. It is
// deliberately a thin wrapper so a future migration (different storage,
// version bump, encryption) only touches this file.
//
// The previous comment in `chat.ts` claimed this adapter existed. It
// didn't — drafts were lost on reload. This is the missing implementation.
//
// Storage key is namespaced + versioned so a future shape change can
// migrate cleanly.

import type { useChatStore } from '@/stores/chat';

const KEY = 'cf-chat:drafts:v1';

type DraftMap = Record<string, { content: string; replyToId: string | null }>;

/** Read the persisted drafts from localStorage. Returns `{}` on any
 *  parse failure so a corrupted blob doesn't crash the composer. */
export function loadDrafts(): DraftMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as DraftMap;
  } catch {
    return {};
  }
}

/** Persist the current drafts map. Fails silently if storage is
 *  unavailable (Safari private mode, quota exceeded). */
export function saveDrafts(map: DraftMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // ignore — best-effort persistence
  }
}

/** Subscribe to store changes and write through. Returns an unsubscribe
 *  function so the caller (typically a top-level effect in ChatPage) can
 *  tear it down on unmount.
 *
 *  The selector returns the drafts slice only, so unrelated store
 *  changes (typing indicators, presence, etc.) don't trigger a write. */
export function bindDraftsPersistence(store: typeof useChatStore): () => void {
  // Seed the in-memory store from disk before subscribing, so drafts that
  // survived a reload are picked up before the first render commits.
  const seeded = loadDrafts();
  if (Object.keys(seeded).length > 0) {
    store.setState({ draftsByConversation: seeded });
  }
  return store.subscribe((s) => s.draftsByConversation, saveDrafts, {
    equalityFn: (a, b) => {
      // Reference equality on the Record is fine — setDraft/clearDraft
      // build new objects every time.
      return a === b;
    },
    fireImmediately: false,
  });
}
